import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Card,
  CardContent,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  Divider,
  //test
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
  useMediaQuery,
  Button,
  Stack,
  Alert,
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  AreaChart,
  Area,
  ComposedChart,
} from 'recharts';
import { getSummary, getTransactions } from '../api';
import { formatCurrency, formatDateShort } from '../utils/formatters';
import toast from 'react-hot-toast';
import { PhoneAndroid, AccountBalance, Refresh, FilterList, Clear } from '@mui/icons-material';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658', '#ff6b6b'];
const SOURCE_COLORS = {
  mpesa: '#4CAF50',
  bank: '#1976d2',
};

const Analytics = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [summary, setSummary] = useState(null);
  const [allTransactions, setAllTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [sourceFilter, setSourceFilter] = useState('all');
  const [filteredSummary, setFilteredSummary] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (allTransactions.length > 0) {
      applyFilters();
    }
  }, [sourceFilter, allTransactions]);

  const fetchData = async () => {
    try {
      setLoading(true);

      // Fetch summary
      const summaryResponse = await getSummary();
      setSummary(summaryResponse.data);

      // Fetch all transactions for filtering
      const transactionsResponse = await getTransactions({ limit: 10000 });
      setAllTransactions(transactionsResponse.data || []);

    } catch (error) {
      toast.error('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...allTransactions];

    // Apply source filter
    if (sourceFilter !== 'all') {
      filtered = filtered.filter(t => t.source_type === sourceFilter);
    }

    setFilteredTransactions(filtered);

    // Calculate filtered summary
    const totalTransactions = filtered.length;
    const totalPaidIn = filtered.reduce((sum, t) => sum + (parseFloat(t.paid_in) || 0), 0);
    const totalWithdrawn = filtered.reduce((sum, t) => sum + (parseFloat(t.withdrawn) || 0), 0);
    const creditCount = filtered.filter(t => t.transaction_type === 'Credit').length;
    const debitCount = filtered.filter(t => t.transaction_type === 'Debit').length;
    const mpesaCount = filtered.filter(t => t.source_type === 'mpesa').length;
    const bankCount = filtered.filter(t => t.source_type === 'bank').length;

    // Daily summary
    const dailyMap = {};
    filtered.forEach(t => {
      const date = t.completion_time ? new Date(t.completion_time).toISOString().split('T')[0] : null;
      if (date) {
        if (!dailyMap[date]) {
          dailyMap[date] = { date, count: 0, paid_in: 0, withdrawn: 0, mpesa: 0, bank: 0 };
        }
        dailyMap[date].count += 1;
        dailyMap[date].paid_in += parseFloat(t.paid_in) || 0;
        dailyMap[date].withdrawn += parseFloat(t.withdrawn) || 0;
        if (t.source_type === 'mpesa') dailyMap[date].mpesa += 1;
        if (t.source_type === 'bank') dailyMap[date].bank += 1;
      }
    });

    const dailySummary = Object.values(dailyMap).sort((a, b) => a.date.localeCompare(b.date));

    // Category breakdown
    const categoryMap = {};
    filtered.forEach(t => {
      const category = t.transaction_category || 'Other';
      if (!categoryMap[category]) {
        categoryMap[category] = { count: 0, amount: 0, source: t.source_type || 'unknown' };
      }
      categoryMap[category].count += 1;
      categoryMap[category].amount += parseFloat(t.paid_in) || 0;
    });

    const categoryBreakdown = Object.entries(categoryMap).map(([name, data]) => ({
      name,
      count: data.count,
      amount: data.amount,
      source: data.source,
    }));

    // Monthly summary
    const monthlyMap = {};
    filtered.forEach(t => {
      const date = t.completion_time ? new Date(t.completion_time) : null;
      if (date) {
        const month = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        if (!monthlyMap[month]) {
          monthlyMap[month] = { month, count: 0, paid_in: 0, withdrawn: 0 };
        }
        monthlyMap[month].count += 1;
        monthlyMap[month].paid_in += parseFloat(t.paid_in) || 0;
        monthlyMap[month].withdrawn += parseFloat(t.withdrawn) || 0;
      }
    });

    const monthlySummary = Object.values(monthlyMap).sort((a, b) => a.month.localeCompare(b.month));

    setFilteredSummary({
      totalTransactions,
      totalPaidIn,
      totalWithdrawn,
      creditCount,
      debitCount,
      mpesaCount,
      bankCount,
      dailySummary,
      categoryBreakdown,
      monthlySummary,
      firstTransaction: filtered.length > 0 ? filtered[filtered.length - 1]?.completion_time : null,
      lastTransaction: filtered.length > 0 ? filtered[0]?.completion_time : null,
    });
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleSourceFilterChange = (event) => {
    setSourceFilter(event.target.value);
  };

  const clearFilter = () => {
    setSourceFilter('all');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Use filtered summary if available, otherwise use the original summary
  const data = filteredSummary || summary;
  const stats = data || {};
  const dailyData = data?.dailySummary?.map(day => ({
    date: formatDateShort(day.date),
    credits: day.paid_in || 0,
    debits: day.withdrawn || 0,
    count: day.count || 0,
  })) || [];

  const categoryData = data?.categoryBreakdown || [];
  const monthlyData = data?.monthlySummary || [];

  // Source breakdown
  const sourceBreakdown = [
    { name: 'M-Pesa', value: stats.mpesaCount || 0, color: '#4CAF50' },
    { name: 'Bank', value: stats.bankCount || 0, color: '#1976d2' },
  ];

  // Transaction type data
  const transactionTypeData = [
    { name: 'Credit', value: stats.creditCount || 0 },
    { name: 'Debit', value: stats.debitCount || 0 },
  ];

  // Top merchants
  const merchantData = filteredTransactions
    .filter(t => t.merchant_business_name)
    .reduce((acc, t) => {
      const name = t.merchant_business_name;
      if (!acc[name]) {
        acc[name] = { name, amount: 0, count: 0 };
      }
      acc[name].amount += Math.abs(parseFloat(t.withdrawn) || 0);
      acc[name].count += 1;
      return acc;
    }, {});

  const topMerchants = Object.values(merchantData)
    .sort((a, b) => b.amount - a.amount)
    .slice(0, 10);

  const getSourceLabel = (source) => {
    if (source === 'mpesa') return 'M-Pesa';
    if (source === 'bank') return 'Bank';
    return 'All';
  };

  const formatCurrencyLocal = (amount) => {
    if (!amount) return 'KES 0';
    return formatCurrency(amount);
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between',
        alignItems: { xs: 'stretch', sm: 'center' },
        mb: 3,
        gap: 2
      }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Analytics
          </Typography>
          <Typography variant="subtitle1" color="text.secondary" gutterBottom>
            Detailed insights into your transactions
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 1 }}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Source</InputLabel>
            <Select
              value={sourceFilter}
              onChange={handleSourceFilterChange}
              label="Source"
            >
              <MenuItem value="all">All Sources</MenuItem>
              <MenuItem value="mpesa">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PhoneAndroid sx={{ fontSize: 18, color: '#4CAF50' }} />
                  M-Pesa
                </Box>
              </MenuItem>
              <MenuItem value="bank">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AccountBalance sx={{ fontSize: 18, color: '#1976d2' }} />
                  Bank
                </Box>
              </MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchData}
            size="small"
          >
            Refresh
          </Button>
          {sourceFilter !== 'all' && (
            <Button
              variant="text"
              size="small"
              onClick={clearFilter}
              startIcon={<Clear />}
              color="error"
            >
              Clear Filter
            </Button>
          )}
        </Stack>
      </Box>

      {/* Source Filter Badge */}
      {sourceFilter !== 'all' && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Viewing <strong>{getSourceLabel(sourceFilter)}</strong> transactions only
          <Button size="small" onClick={clearFilter} sx={{ ml: 2 }}>
            Show All
          </Button>
        </Alert>
      )}

      {/* KPI Cards */}
      <Grid container spacing={isMobile ? 1.5 : 3} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom variant="caption">
                Total Transactions
              </Typography>
              <Typography variant="h5">
                {stats.totalTransactions || 0}
              </Typography>
              {sourceFilter === 'all' && (
                <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                  <Chip size="small" label={`M-Pesa: ${stats.mpesaCount || 0}`} sx={{ fontSize: '0.6rem', height: 20, bgcolor: '#4CAF50', color: 'white' }} />
                  <Chip size="small" label={`Bank: ${stats.bankCount || 0}`} sx={{ fontSize: '0.6rem', height: 20, bgcolor: '#1976d2', color: 'white' }} />
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom variant="caption">
                Total Paid In
              </Typography>
              <Typography variant="h5" color="success.main">
                {formatCurrencyLocal(stats.totalPaidIn || 0)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom variant="caption">
                Total Withdrawn
              </Typography>
              <Typography variant="h5" color="error.main">
                {formatCurrencyLocal(stats.totalWithdrawn || 0)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom variant="caption">
                Net Balance
              </Typography>
              <Typography variant="h5">
                {formatCurrencyLocal((stats.totalPaidIn || 0) - (stats.totalWithdrawn || 0))}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom variant="caption">
                Sources
              </Typography>
              <Typography variant="h5">
                {sourceBreakdown.filter(s => s.value > 0).length}
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                {sourceBreakdown.filter(s => s.value > 0).map((s) => (
                  <Chip
                    key={s.name}
                    size="small"
                    label={s.name}
                    sx={{ fontSize: '0.6rem', height: 20, bgcolor: s.color, color: 'white' }}
                  />
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
          variant={isMobile ? 'scrollable' : 'standard'}
          scrollButtons="auto"
        >
          <Tab label="Overview" />
          <Tab label="Daily Trends" />
          <Tab label="Categories" />
          <Tab label="Monthly" />
          <Tab label="Merchants" />
        </Tabs>

        {/* Overview Tab */}
        {tabValue === 0 && (
          <Grid container spacing={3} sx={{ p: 3 }}>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                Transaction Distribution
              </Typography>
              <Box sx={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={transactionTypeData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {transactionTypeData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                Source Breakdown
              </Typography>
              <Box sx={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={sourceBreakdown.filter(s => s.value > 0)}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {sourceBreakdown.filter(s => s.value > 0).map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
            </Grid>
          </Grid>
        )}

        {/* Daily Trends Tab */}
        {tabValue === 1 && (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Daily Transaction Trends {sourceFilter !== 'all' && `(${getSourceLabel(sourceFilter)})`}
            </Typography>
            <Box sx={{ height: 400 }}>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={dailyData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis yAxisId="left" />
                  <YAxis yAxisId="right" orientation="right" />
                  <Tooltip
                    formatter={(value, name) => {
                      if (name === 'Credits' || name === 'Debits') return formatCurrencyLocal(value);
                      return value;
                    }}
                  />
                  <Legend />
                  <Bar yAxisId="left" dataKey="credits" fill="#4caf50" name="Credits" />
                  <Bar yAxisId="left" dataKey="debits" fill="#f44336" name="Debits" />
                  <Line yAxisId="right" type="monotone" dataKey="count" stroke="#2196f3" name="Transaction Count" />
                </ComposedChart>
              </ResponsiveContainer>
            </Box>
          </Box>
        )}

        {/* Categories Tab */}
        {tabValue === 2 && (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Category Breakdown {sourceFilter !== 'all' && `(${getSourceLabel(sourceFilter)})`}
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                <Box sx={{ height: 400 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={categoryData.slice(0, 10)}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip formatter={(value) => formatCurrencyLocal(value)} />
                      <Legend />
                      <Bar dataKey="amount" fill="#8884d8" name="Total Amount" />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle1" gutterBottom>
                  Category Summary
                </Typography>
                <List>
                  {categoryData.slice(0, 5).map((cat, index) => (
                    <ListItem key={index}>
                      <ListItemText
                        primary={cat.name}
                        secondary={`${cat.count} transactions`}
                      />
                      <Chip
                        label={formatCurrencyLocal(cat.amount)}
                        size="small"
                        color="primary"
                      />
                    </ListItem>
                  ))}
                </List>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Monthly Tab */}
        {tabValue === 3 && (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Monthly Summary {sourceFilter !== 'all' && `(${getSourceLabel(sourceFilter)})`}
            </Typography>
            <Box sx={{ height: 400 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={monthlyData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip formatter={(value) => formatCurrencyLocal(value)} />
                  <Legend />
                  <Line type="monotone" dataKey="paid_in" stroke="#4caf50" name="Paid In" />
                  <Line type="monotone" dataKey="withdrawn" stroke="#f44336" name="Withdrawn" />
                  <Line type="monotone" dataKey="count" stroke="#2196f3" name="Transactions" strokeDasharray="5 5" />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </Box>
        )}

        {/* Merchants Tab */}
        {tabValue === 4 && (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Top Merchants {sourceFilter !== 'all' && `(${getSourceLabel(sourceFilter)})`}
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                <Box sx={{ height: 400 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={topMerchants}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip formatter={(value) => formatCurrencyLocal(value)} />
                      <Legend />
                      <Bar dataKey="amount" fill="#8884d8" name="Total Amount" />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle1" gutterBottom>
                  Merchant Summary
                </Typography>
                <List>
                  {topMerchants.slice(0, 5).map((merchant, index) => (
                    <ListItem key={index}>
                      <ListItemText
                        primary={merchant.name}
                        secondary={`${merchant.count} transactions`}
                      />
                      <Chip
                        label={`#${index + 1}`}
                        size="small"
                        color="primary"
                      />
                    </ListItem>
                  ))}
                </List>
              </Grid>
            </Grid>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default Analytics;